import { Component, OnInit, AfterViewInit, HostListener, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { FooterComponent } from '../../shared/components/footer/footer.component';

interface SectionPoint {
  serialNumber: string;
  text: string;
  children?: SectionPoint[];
}

interface Section {
  serialNumber: string;
  label: string;
  description: SectionPoint[];
}

interface Category {
  label: string;
}

@Component({
  selector: 'app-privacy-policy',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HeaderComponent,
    FooterComponent
  ],
  templateUrl: './privacy-policy.component.html',
  styleUrl: './privacy-policy.component.scss',
})
export class PrivacyPolicyComponent implements OnInit, AfterViewInit {
  @ViewChild('scrollContainer') scrollContainer?: ElementRef<HTMLElement>;

  activeCategory = 'Introduction: MahaAgX and Privacy Policy';
  selectedItem: string | null = null;
  isStickyDropdown = false;

  sections: Section[] = [
    {
      serialNumber: '1',
      label: 'Introduction: MahaAgX and Privacy Policy',
      description: [
        {
          serialNumber: '1.1',
          text: 'The Maharashtra Agriculture Data Exchange ("MahaAgX") is a platform operated by the Department of Agriculture, Government of Maharashtra, that seeks to foster artificial intelligence ("AI") innovation by providing access to data (that is not personal data), foundational AI models, and compute power.'
        },
        {
          serialNumber: '1.2',
          text: 'This privacy policy ("Privacy Policy") governs the collection, use, and sharing of data of Users (including individuals authorised to act on behalf of Users that are companies or organisations) in the course of their use of MahaAgX. The Privacy Policy must be read in conjunction with the MahaAgX Governance and Use Framework ("Framework") available on <a href="https://mahaagx.maharashtra.gov.in/terms-of-service" target="_blank" style="color: #007bff; text-decoration: underline;">https://mahaagx.maharashtra.gov.in/terms-of-service</a>.'
        },
        {
          serialNumber: '1.3',
          text: 'The Privacy Policy applies to the Operator, Providers, and Users of MahaAgX. It is compliant with, subject to, and superseded by any applicable laws notified by the Government of Maharashtra and the Government of India. In the event the User (including Providers) does not agree to the terms of this Privacy Policy, the User must immediately discontinue their use of MahaAgX. Before proceeding with the registration and account creation on MahaAgX, the User is required to review and consent to this Privacy Policy. If the User does not provide such consent, the account creation cannot be completed, and the Operator is not liable to provide access to MahaAgX or its Functionalities to such User.'
        },
        {
          serialNumber: '1.4',
          text: 'Terms used or capitalised but not defined in this Privacy Policy have the meaning assigned to them in the Framework.'
        }
      ]
    },
    {
      serialNumber: '2',
      label: 'Data Collected',
      description: [
        {
          serialNumber: '2.1',
          text: `MahaAgX does not collect or store any personal data except when necessary for Users' registration or account creation, identity verification or authentication, availing any support services, and discharging grievance redressal function under the Framework and applicable laws. Any personal data so collected or stored is with the relevant Users' consent.`
        },
        {
          serialNumber: '2.2',
          text: 'Data Collected Automatically<br>Upon a User accessing MahaAgX, the following data is automatically collected and stored:',
          children: [
            {
              serialNumber: 'a',
              text: 'Website traffic data: User visits to, and interactions and engagements on, MahaAgX.'
            },
            {
              serialNumber: 'b',
              text: 'Device data: whether Users are accessing MahaAgX via mobile, tablet, or desktop, along with browser, location, IP address, and operating software details.'
            }
          ]
        },
        {
          serialNumber: '2.3',
          text: 'Data Collected Upon Registration',
          children: [
            {
              serialNumber: 'a',
              text: 'Data is collected and stored from and with the prior consent of the User, at the time and for the purposes of registration or account creation and identity verification or authentication. This data may include the following:',
              children: [
                { serialNumber: 'i', text: 'Full name, email address, date of birth, gender, address, and phone number;' },
                { serialNumber: 'ii', text: "name of the organisation, job title, employee identity number/ card, organisation type (e.g. private or public sector), sector of operation, website, organisations' incorporation and operational documents, letter of authorisation;" },
                { serialNumber: 'iii', text: 'username, profile picture or photograph, designation (i.e. whether User or Provider on MahaAgX).' }
              ]
            },
            {
              serialNumber: 'b',
              text: `The registration or account creation and login process is undertaken only with User consent.`
            }
          ]
        },
        {
          serialNumber: '2.4',
          text: 'Users are expressly prohibited from making available any personal data on MahaAgX, except as required for registration or account creation, identity verification or authentication, availing any support services, or submitting their grievances under this Privacy Policy, the Framework or applicable laws.'
        },
        {
          serialNumber: '2.5',
          text: 'Any personal data made available by Users, such as while providing feedback on MahaAgX or details of their social media profiles may be collected or stored by MahaAgX only with User consent.'
        }
      ]
    },
    {
      serialNumber: '3',
      label: 'Processing and Use of Data',
      description: [
        {
          serialNumber: '3.1',
          text: 'MahaAgX processes all data (whether personal or not personal) in accordance with applicable laws.'
        },
        {
          serialNumber: '3.2',
          text: 'MahaAgX requires all Users not to utilise the data made available through MahaAgX in any manner violative of applicable laws, including data protection laws.'
        },
        {
          serialNumber: '3.3',
          text: 'MahaAgX requires all Providers to ensure that all data contributed by them is appropriately secured and compliant with applicable laws. Where anonymisation techniques are employed, the Provider must ensure that such techniques are robust and secure to prevent re-identification of the individual to whom such data pertains.'
        },
        {
          serialNumber: '3.4',
          text: 'Key uses of the data collected by MahaAgX are:',
          children: [
            {
              serialNumber: 'a',
              text: 'creating an account or registering any User on MahaAgX (including identity verification or authentication);'
            },
            { serialNumber: 'b', text: 'logging in to MahaAgX;' },
            {
              serialNumber: 'c',
              text: 'delivering the Functionalities on MahaAgX;'
            },
            {
              serialNumber: 'd',
              text: 'improving the Functionalities on MahaAgX, including better User interface and availability of resources;'
            },
            {
              serialNumber: 'e',
              text: 'monitoring and analysing the activity on MahaAgX for maintaining the security of data and the MahaAgX platform; and'
            },
            {
              serialNumber: 'f',
              text: 'ensuring compliance with applicable laws.'
            }
          ]
        }
      ]
    },
    {
      serialNumber: '4',
      label: 'Sharing and Disclosure of Data',
      description: [
        {
          serialNumber: '4.1',
          text: 'MahaAgX does not share or disclose any personal data of Users with any other person, except:',
          children: [
            {
              serialNumber: 'a',
              text: 'such data as is necessary for identity verification or authentication and as the User consents to, with MeriPehchan and DigiLocker; and'
            },
            {
              serialNumber: 'b',
              text: ' such data as is required under applicable law or with the prior consent of the User.'
            }
          ]
        },
        {
          serialNumber: '4.2',
          text: 'MahaAgX shares and discloses other data (that is not personal data) made available by Providers on MahaAgX only in accordance with the Framework and applicable laws.'
        }
      ]
    },
    {
      serialNumber: '5',
      label: 'Data Retention',
      description: [
        {
          serialNumber: '5.1',
          text: 'MahaAgX retains personal data for as long as such data is necessary to provide access to MahaAgX for a User and to comply with applicable laws.'
        }
      ]
    },
    {
      serialNumber: '6',
      label: 'Withdrawal of Consent',
      description: [
        {
          serialNumber: '6.1',
          text: 'Users may withdraw their consent for the processing of their personal data at any time by sending MahaAgX an email at <a href="mailto:support-mahaagx-agri@maharashtra.gov.in" style="color: #007bff; text-decoration: underline;">support-mahaagx-agri@maharashtra.gov.in</a>. Upon receipt of a request, MahaAgX will stop further processing of their personal data in accordance with applicable law.'
        },
        {
          serialNumber: '6.2',
          text: 'Upon receipt of such withdrawal of consent from a User, MahaAgX will erase such personal data in accordance with applicable laws.'
        }
      ]
    },
    {
      serialNumber: '7',
      label: 'Rights of Users',
      description: [
        {
          serialNumber: '7.1',
          text: 'Users have the right to access their accounts on MahaAgX at any time and add, modify, or erase any personal data which they have previously consented to share subject to applicable laws.'
        }
      ]
    },
    {
      serialNumber: '8',
      label: 'Security of Data',
      description: [
        {
          serialNumber: '8.1',
          text: 'MahaAgX seeks to ensure data security by requiring all Users to establish such technological and operational safeguards for cybersecurity as may be prescribed in the Framework, including any SOP(s).'
        },
        {
          serialNumber: '8.2',
          text: 'MahaAgX has adequate network security, application security, data security, and integration security measures to protect personal data and confidential information shared on the platform.'
        }
      ]
    },
    {
      serialNumber: '9',
      label: 'Grievance Redressal',
      description: [
        {
          serialNumber: '9.1',
          text: 'For the redressal of any grievance arising in connection with this Privacy Policy or the use of any data thereunder, the User may reach out to the grievance officer of the Operator ("<strong>Grievance Officer</strong>"):<br><br>Name of the Grievance Officer : [Name]<br>E-mail ID: <a href="mailto:grievance-mahaagx-agri@maharashtra.gov.in" style="color: #007bff; text-decoration: underline;">grievance-mahaagx-agri@maharashtra.gov.in</a>'
        }
      ]
    },
    {
      serialNumber: '10',
      label: 'Modifications and Amendments',
      description: [
        {
          serialNumber: '10.1',
          text: 'The Operator may review and amend or modify this Privacy Policy periodically, which will be effective within 7 days from the date of posting such revised terms.'
        },
        {
          serialNumber: '10.2',
          text: 'Users are requested to read, understand, and consent to such amended or modified Privacy Policy, which will apply to the Operator, Providers, and Users of MahaAgX once it is effective. In the event Users (including Providers) do not agree to such amended or modified Privacy Policy, Users must discontinue their use of MahaAgX. MahaAgX will not be liable to the User for discontinuance or suspension of access to MahaAgX in the event the User does not consent to such revised terms.'
        },
        {
          serialNumber: '10.3',
          text: 'Any actions undertaken by the Operator or the Grievance Officer under a previous version of the Privacy Policy will not be affected by any subsequent modifications or amendments.'
        }
      ]
    },
    {
      serialNumber: '11',
      label: 'Governing Law and Jurisdiction',
      description: [
        {
          serialNumber: '11.1',
          text: 'This Privacy Policy will be governed by and construed in accordance with the laws of India. Any disputes arising out of or in connection with this Privacy Policy will be subject to the exclusive jurisdiction of the courts of Maharashtra.'
        }
      ]
    }
  ];

  categories: Category[] = [
    { label: 'Introduction: MahaAgX and Privacy Policy' },
    { label: 'Data Collected' },
    { label: 'Processing and Use of Data' },
    { label: 'Sharing and Disclosure of Data' },
    { label: 'Data Retention' },
    { label: 'Withdrawal of Consent' },
    { label: 'Rights of Users' },
    { label: 'Security of Data' },
    { label: 'Grievance Redressal' },
    { label: 'Modifications and Amendments' },
    { label: 'Governing Law and Jurisdiction' }
  ];

  constructor(private router: Router) {}

  ngOnInit(): void {
    this.patchTermsOfServiceLink();

    // Initialize selected item and active category to first category
    this.selectedItem = this.categories[0].label;
    this.activeCategory = this.categories[0].label;
    
    // Scroll to top when component loads to ensure first section is visible
    setTimeout(() => {
      this.scrollToTop();
    }, 0);
  }

  private patchTermsOfServiceLink(): void {
    const point = this.sections
      .find((section) => section.serialNumber === '1')
      ?.description.find((description) => description.serialNumber === '1.2');
    if (!point) {
      return;
    }

    const termsPath = this.router.createUrlTree(['/terms-of-service']).toString();
    point.text =
      'This privacy policy ("Privacy Policy") governs the collection, use, and sharing of data of Users (including individuals authorised to act on behalf of Users that are companies or organisations) in the course of their use of MahaAgX. The Privacy Policy must be read in conjunction with the MahaAgX Governance and Use Framework ("Framework") — ' +
      `<a href="${termsPath}" style="color: #007bff; text-decoration: underline;">click here</a>.`;
  }

  ngAfterViewInit(): void {
    this.updateActiveCategoryOnScroll();
  }

  /** Called by (scroll) on section and by window:scroll on smaller screens */
  onScroll(): void {
    const container = this.scrollContainer?.nativeElement;
    const scrollPosition = (container && this.isSectionScrollContainer(container))
      ? container.scrollTop
      : (window.pageYOffset ?? document.documentElement.scrollTop);
    this.isStickyDropdown = scrollPosition > 100;
    this.updateActiveCategoryOnScroll();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.onScroll();
  }

  private isSectionScrollContainer(container: HTMLElement | undefined): boolean {
    return !!container && container.scrollHeight > container.clientHeight;
  }

  updateActiveCategoryOnScroll(): void {
    const offset = 150; // Offset to determine which section is active (matches scrollToSection offset)
    const container = this.scrollContainer?.nativeElement;
    const useSectionScroll = this.isSectionScrollContainer(container);
    const scrollPosition = useSectionScroll
      ? (container!.scrollTop)
      : (window.pageYOffset ?? document.documentElement.scrollTop);

    for (let i = this.sections.length - 1; i >= 0; i--) {
      const section = this.sections[i];
      const sectionId = this.sanitizeId(section.label);
      const element = document.getElementById(sectionId);
      if (!element) continue;

      let elementTop: number;
      if (useSectionScroll && container) {
        // Section is scroll container: position relative to scrollable content
        elementTop = container.scrollTop + (element.getBoundingClientRect().top - container.getBoundingClientRect().top);
      } else {
        const elementRect = element.getBoundingClientRect();
        elementTop = elementRect.top + scrollPosition;
      }

      if (scrollPosition + offset >= elementTop) {
        this.activeCategory = section.label;
        this.selectedItem = section.label;
        break;
      }
    }
  }

  onDropdownChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const selectedLabel = target.value;
    if (selectedLabel) {
      this.isStickyDropdown = true;
      this.scrollToSection(selectedLabel);
    }
  }

  filterSections(section: string): void {
    this.activeCategory = section;
    this.scrollToSection(section);
  }

  private scrollToTop(): void {
    const container = this.scrollContainer?.nativeElement;
    if (container && this.isSectionScrollContainer(container)) {
      container.scrollTo({ top: 0, behavior: 'auto' });
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }

  scrollToSection(label: string): void {
    const sectionId = this.sanitizeId(label);
    const element = document.getElementById(sectionId);
    if (!element) return;

    const offset = 150;
    const container = this.scrollContainer?.nativeElement;
    const useSectionScroll = this.isSectionScrollContainer(container);

    if (useSectionScroll && container) {
      // Scroll within the section container so right sidebar highlights match
      const elementTopInContent = container.scrollTop + (element.getBoundingClientRect().top - container.getBoundingClientRect().top);
      const targetScrollTop = Math.max(0, elementTopInContent - offset);
      container.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
      return;
    }

    // Window scroll (e.g. smaller screens)
    const elementRect = element.getBoundingClientRect();
    const elementTop = elementRect.top + window.pageYOffset;
    const elementBottom = elementTop + elementRect.height;
    const offsetPosition = elementTop - offset;
    const sectionIndex = this.sections.findIndex(s => s.label === label);
    const isLastSection = sectionIndex === this.sections.length - 1;
    const viewportHeight = window.innerHeight;

    let finalScroll = offsetPosition;
    if (isLastSection) {
      const maxScrollForSection = elementBottom - viewportHeight;
      finalScroll = Math.min(offsetPosition, Math.max(offsetPosition, maxScrollForSection));
      finalScroll = Math.min(finalScroll, elementBottom - viewportHeight);
    }

    window.scrollTo({ top: finalScroll, behavior: 'smooth' });
  }

  navigateToHome(): void {
    this.router.navigate(['/']);
  }

  sanitizeId(label: string): string {
    // More robust ID sanitization
    return label
      .toLowerCase()
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters except spaces
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  }
}
